import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import type { DiceType } from '@/types/dice'

import '@/styles/shared-dice-roll.css'

/**
 * Um dado, em cubo — CSS 3D puro, sem biblioteca.
 *
 * Portado do protótipo do Paulo (`docs/prototipos/dados-bandeja.html`), que era
 * a referência visual desde o começo: seis faces posicionadas com `translateZ`,
 * o cubo gira para parar com a face sorteada de frente, e o d6 mostra pontos em
 * vez de número.
 *
 * **É o mesmo componente na bandeja e na cerimônia.** Isso é de propósito: o
 * módulo de dados já foi mordido três vezes pelo mesmo padrão — a jogada é
 * desenhada em telas diferentes, e uma delas fica para trás. Cada dado que
 * passa a sair daqui é uma tela a menos para esquecer.
 *
 * **O cubo não imita a forma do dado, e isso é decisão e não limitação**
 * (Paulo, 2026-07-25): um cubo atende, desde que respeite duas regras que o
 * fazem se ler como o dado certo —
 *
 *   1. **nenhum valor repetido entre as faces mostradas**, para o dado não
 *      parecer viciado nem confundir quem olha de perto;
 *   2. **a face em foco, com o dado em repouso, mostra o MÁXIMO daquele dado**
 *      — um cubo com 20 na frente se lê como d20; com 6, como d6. É assim que
 *      um cubo diz que dado ele é, sem precisar ter vinte faces.
 *
 * Rolado, a face em foco passa a mostrar o valor sorteado. Um d4 é o único que
 * não fecha as seis faces sem repetir (tem só quatro valores): ali a repetição
 * é mínima e vai para as faces do fundo.
 */

/**
 * Cor por tipo de dado — a mesma família do protótipo, usada pelos botões da
 * paleta (texto e borda coloridos).
 */
export const COR_DO_DADO: Record<DiceType, string> = {
  D4: 'text-violet-400 border-violet-400',
  D6: 'text-slate-300 border-slate-300',
  D8: 'text-orange-400 border-orange-400',
  D10: 'text-emerald-400 border-emerald-400',
  D12: 'text-pink-400 border-pink-400',
  D20: 'text-sky-400 border-sky-400',
  D100: 'text-amber-400 border-amber-400',
}

/**
 * Só a borda, para as faces do cubo.
 *
 * O número (e os pontos do d6) fica claro e a borda é que carrega a cor do
 * dado — como no protótipo. Aplicar `COR_DO_DADO` aqui colocaria duas classes
 * de cor de texto na mesma face, e no Tailwind quem vence é a ordem da folha
 * de estilo, não a do atributo: a cor sairia imprevisível.
 */
const BORDA_DO_DADO: Record<DiceType, string> = {
  D4: 'border-violet-400',
  D6: 'border-slate-300',
  D8: 'border-orange-400',
  D10: 'border-emerald-400',
  D12: 'border-pink-400',
  D20: 'border-sky-400',
  D100: 'border-amber-400',
}

const LADOS: Record<string, number> = {
  D4: 4, D6: 6, D8: 8, D10: 10, D12: 12, D20: 20, D100: 100,
}

/** Onde cada ponto do d6 mora na grade 3x3 (1..9). */
const PONTOS: Record<number, number[]> = {
  1: [5], 2: [1, 9], 3: [1, 5, 9], 4: [1, 3, 7, 9], 5: [1, 3, 5, 7, 9], 6: [1, 3, 4, 6, 7, 9],
}

/**
 * As seis faces: a transformação que põe cada uma no lugar, e a rotação que o
 * cubo precisa aplicar para trazer aquela face para a frente.
 */
const FACES: { transform: string; x: number; y: number }[] = [
  { transform: 'rotateY(0deg)', x: 0, y: 0 },
  { transform: 'rotateY(180deg)', x: 0, y: -180 },
  { transform: 'rotateY(90deg)', x: 0, y: -90 },
  { transform: 'rotateY(-90deg)', x: 0, y: 90 },
  { transform: 'rotateX(90deg)', x: -90, y: 0 },
  { transform: 'rotateX(-90deg)', x: 90, y: 0 },
]

/** Pousado, o cubo fica levemente torto — dado na mesa não fica de frente. */
const REPOUSO = 'rotateX(-18deg) rotateY(26deg)'

interface DadoCuboProps {
  tipo: DiceType
  /** Valor sorteado. Ausente = o dado ainda não rolou (mostra o tipo). */
  resultado?: number
  /** Os dados estão no ar: o cubo chacoalha até o resultado chegar. */
  chacoalhando?: boolean
  /**
   * A regra descartou este dado: apaga, mas continua legível.
   *
   * "Continua legível" ganhou um número em 2026-07-27: a opacidade era 0.30 e
   * o dado descartado quase sumia — justamente aquele que a regra existe para
   * mostrar ("manteve o maior" só ensina alguma coisa se der para ver o que
   * ficou de fora). Quem diz "fora da conta" passa a ser o risco, não o
   * apagamento. Mesma correção feita na ficha do chat, e pelo mesmo motivo.
   */
  fora?: boolean
  /** Aresta em pixels (a bandeja usa 52, a cerimônia usa mais). */
  tamanho?: number
  className?: string
}

function embaralhar<T>(itens: T[]): T[] {
  const copia = [...itens]
  for (let i = copia.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copia[i], copia[j]] = [copia[j], copia[i]]
  }
  return copia
}

/**
 * As seis faces: `destaque` na face em foco, e valores DISTINTOS nas outras.
 *
 * Dado com seis lados ou mais fecha as seis faces sem repetir nenhum valor.
 * O d4 tem só quatro valores para seis faces: sobra repetir, e a repetição vai
 * para o fim da lista — as faces do fundo, que quase não aparecem.
 */
function valoresDasFaces(lados: number, destaque: number, slotDestaque: number): number[] {
  const disponiveis = embaralhar(
    Array.from({ length: lados }, (_, i) => i + 1).filter(v => v !== destaque)
  )
  // d4: 3 valores para 5 faces. Cicla a lista em vez de repetir sempre o mesmo,
  // para a repeticao ficar espalhada — e nunca encosta no destaque, que
  // aparecer duas vezes e' o que dá cara de dado viciado.
  const candidatos = Array.from(
    { length: 5 },
    (_, i) => disponiveis[i % disponiveis.length]
  )

  const faces: number[] = []
  let proximo = 0
  for (let slot = 0; slot < 6; slot++) {
    faces.push(slot === slotDestaque ? destaque : candidatos[proximo++])
  }
  return faces
}

export function DadoCubo({
  tipo,
  resultado,
  chacoalhando = false,
  fora = false,
  tamanho = 52,
  className,
}: DadoCuboProps) {
  const lados = LADOS[tipo] ?? 6
  const semMovimento = useMemo(
    () => typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  )

  // Cada rolagem acumula voltas inteiras, para o cubo girar de verdade em vez
  // de tomar o caminho curto ate a face — o mesmo truque do prototipo.
  const voltas = useRef(0)
  // Em repouso o cubo mostra a face 0, e ela carrega o MAXIMO do dado: e assim
  // que um cubo se apresenta como d20 ou como d6.
  const [alvo, setAlvo] = useState(0)
  const [faces, setFaces] = useState<number[]>(() => valoresDasFaces(lados, lados, 0))

  useEffect(() => {
    if (resultado === undefined) {
      setAlvo(0)
      setFaces(valoresDasFaces(lados, lados, 0))
      return
    }
    const novoAlvo = Math.floor(Math.random() * 6)
    voltas.current += 1080
    setAlvo(novoAlvo)
    setFaces(valoresDasFaces(lados, resultado, novoAlvo))
  }, [resultado, lados])

  const transform = resultado === undefined
    ? REPOUSO
    : `rotateX(${FACES[alvo].x - voltas.current}deg) rotateY(${FACES[alvo].y + voltas.current}deg)`

  const meia = tamanho / 2
  const borda = BORDA_DO_DADO[tipo] ?? BORDA_DO_DADO.D6

  return (
    <div
      className={cn('shrink-0', fora && 'opacity-55', className)}
      style={{ perspective: tamanho * 10, width: tamanho, height: tamanho }}
      // O valor fica no rótulo: quem lê por leitor de tela (ou por teste) não
      // depende de adivinhar qual das seis faces está de frente.
      aria-label={resultado === undefined ? tipo.toLowerCase() : `${tipo.toLowerCase()}: ${resultado}`}
      data-testid="dado-cubo"
      data-valor={resultado}
    >
      <div
        className={cn(chacoalhando && !semMovimento && 'dado-chacoalhando')}
        data-testid={chacoalhando ? 'dado-chacoalhando' : undefined}
        style={{
          width: tamanho,
          height: tamanho,
          position: 'relative',
          transformStyle: 'preserve-3d',
          // Enquanto chacoalha, quem manda no transform e a animacao do CSS.
          transform: chacoalhando && !semMovimento ? undefined : transform,
          transition: semMovimento ? 'none' : 'transform 1.15s cubic-bezier(.18,.72,.24,1)',
        }}
      >
        {faces.map((valor, slot) => (
          <div
            key={slot}
            aria-hidden="true"
            className={cn(
              'absolute flex items-center justify-center rounded-lg border-2',
              'bg-zinc-800/90 font-mono font-medium text-zinc-100',
              borda,
              fora && 'line-through'
            )}
            style={{
              width: tamanho,
              height: tamanho,
              transform: `${FACES[slot].transform} translateZ(${meia}px)`,
            }}
          >
            {lados === 6 ? (
              <div
                className="grid h-full w-full grid-cols-3 grid-rows-3"
                style={{ padding: tamanho * 0.15 }}
              >
                {Array.from({ length: 9 }, (_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'self-center justify-self-center rounded-full',
                      PONTOS[valor]?.includes(i + 1) && 'bg-current'
                    )}
                    style={{ width: tamanho * 0.15, height: tamanho * 0.15 }}
                  />
                ))}
              </div>
            ) : (
              <span style={{
                fontSize: tamanho * (valor > 99 ? 0.26 : valor > 9 ? 0.33 : 0.38),
              }}>
                {valor}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default DadoCubo
